import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatoMXN, formatoFecha, ESTADOS } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, User, Calendar, Wrench, DollarSign, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function VehiculoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehiculo, setVehiculo] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await api.get(`/vehiculos/${id}`);
        setVehiculo(res.data);
      } catch { toast.error('Error al cargar vehiculo'); navigate('/vehiculos'); }
      finally { setCargando(false); }
    };
    cargar();
  }, [id, navigate]);

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-48 bg-slate-800" /></div>;
  if (!vehiculo) return null;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="vehiculo-detalle-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/vehiculos')} className="text-slate-400 hover:text-white" data-testid="volver-vehiculos-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">
            {vehiculo.marca} {vehiculo.modelo} {vehiculo.anio}
          </h1>
          <span className="font-mono text-amber-500 text-sm">{vehiculo.placas}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Datos del Vehiculo</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-slate-500 uppercase">Marca</p><p className="text-slate-300">{vehiculo.marca}</p></div>
              <div><p className="text-xs text-slate-500 uppercase">Modelo</p><p className="text-slate-300">{vehiculo.modelo}</p></div>
              <div><p className="text-xs text-slate-500 uppercase">Año</p><p className="text-slate-300">{vehiculo.anio || '-'}</p></div>
              <div><p className="text-xs text-slate-500 uppercase">Color</p><p className="text-slate-300">{vehiculo.color || '-'}</p></div>
              <div><p className="text-xs text-slate-500 uppercase">Placas</p><p className="text-slate-300 font-mono">{vehiculo.placas || '-'}</p></div>
              <div><p className="text-xs text-slate-500 uppercase">Kilometraje</p><p className="text-slate-300 font-mono">{vehiculo.kilometraje ? `${Number(vehiculo.kilometraje).toLocaleString()} km` : '-'}</p></div>
            </div>
            {vehiculo.vin && <div className="mt-2"><p className="text-xs text-slate-500 uppercase">VIN</p><p className="text-slate-300 font-mono text-xs break-all">{vehiculo.vin}</p></div>}
            {vehiculo.cliente && (
              <div className="mt-3 p-3 bg-slate-800/40 rounded-sm border border-slate-800">
                <p className="text-xs text-slate-500 uppercase mb-1">Propietario</p>
                <button onClick={() => navigate(`/clientes/${vehiculo.cliente_id}`)} className="text-sm text-amber-500 hover:text-amber-400 flex items-center gap-2" data-testid="ver-propietario-btn">
                  <User className="w-3.5 h-3.5" /> {vehiculo.cliente.nombre_completo}
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-900/40 border-slate-800 lg:col-span-2">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-barlow text-lg text-slate-200">Historial de Servicio</CardTitle>
            <span className="text-xs text-slate-500">{vehiculo.historial?.length || 0} ordenes</span>
          </CardHeader>
          <CardContent>
            {vehiculo.historial?.length > 0 ? (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-800" />
                <div className="space-y-4">
                  {vehiculo.historial.map((o, i) => (
                    <div key={o.id} className="relative pl-10 animate-slideIn" style={{ animationDelay: `${i * 50}ms` }}>
                      <div className={`absolute left-2.5 top-2 w-3 h-3 rounded-full border-2 ${
                        o.estado === 'entregado' ? 'bg-green-500 border-green-400' :
                        o.estado === 'terminado' ? 'bg-emerald-500 border-emerald-400' :
                        o.estado === 'reparando' ? 'bg-amber-500 border-amber-400' :
                        'bg-slate-600 border-slate-500'
                      }`} />
                      <div
                        className="p-4 bg-slate-800/40 rounded-sm border border-slate-800 hover:border-slate-700 cursor-pointer transition-colors"
                        onClick={() => navigate(`/ordenes/${o.id}`)}
                        data-testid={`historial-orden-${o.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="font-mono text-xs text-amber-500 mr-2">{o.numero_orden}</span>
                            <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold ${ESTADOS[o.estado]?.clase}`}>
                              {ESTADOS[o.estado]?.label}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {formatoFecha(o.fecha_ingreso || o.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-300 mb-2">{o.problema_reportado}</p>
                        {o.reparacion_realizada && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Wrench className="w-3 h-3" /> {o.reparacion_realizada}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs">
                          <span className="text-slate-400 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" /> {formatoMXN(o.total)}
                          </span>
                          <span className="text-slate-500 flex items-center gap-1">
                            <User className="w-3 h-3" /> {o.mecanico_nombre}
                          </span>
                          {o.saldo_pendiente > 0 && (
                            <span className="text-orange-400">Saldo: {formatoMXN(o.saldo_pendiente)}</span>
                          )}
                          {o.saldo_pendiente === 0 && o.total > 0 && (
                            <span className="text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Pagado</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">No hay historial de servicio</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
